import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { WelcomeStep } from './WelcomeStep';
import { ClientStep } from './ClientStep';
import { VendorStep } from './VendorStep';
import { TeamStep } from './TeamStep';
import { TourStep } from './TourStep';
import { useOnboarding } from '../../contexts/OnboardingContext';

interface StepConfig {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  optional?: boolean;
}

const steps: StepConfig[] = [
  { id: 'welcome', title: 'Welcome', component: WelcomeStep },
  { id: 'client', title: 'Add Client', component: ClientStep },
  { id: 'vendors', title: 'Add Vendors', component: VendorStep, optional: true },
  { id: 'users', title: 'Invite Team', component: TeamStep, optional: true },
  { id: 'tour', title: 'Quick Tour', component: TourStep },
];

export const OnboardingWizard: React.FC = () => {
  const { shouldShowOnboarding, showTour, currentStep, updateStep, completeOnboarding, closeTour } = useOnboarding();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Initialize step index based on current step from API or tour mode
  useEffect(() => {
    if (showTour) {
      // If showing tour, jump directly to tour step
      const tourIndex = steps.findIndex((s) => s.id === 'tour');
      if (tourIndex >= 0) {
        setCurrentStepIndex(tourIndex);
      }
    } else {
      const stepIndex = steps.findIndex((s) => s.id === currentStep);
      if (stepIndex >= 0) {
        setCurrentStepIndex(stepIndex);
      }
    }
  }, [currentStep, showTour]);

  const handleNext = async () => {
    // If in tour mode and on tour step, just close the tour
    if (showTour && currentStepIndex === steps.findIndex((s) => s.id === 'tour')) {
      closeTour();
      return;
    }

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      await updateStep(steps[nextIndex].id);
      setCurrentStepIndex(nextIndex);
    } else {
      await completeOnboarding();
    }
  };

  const handleSkip = async () => {
    await handleNext();
  };

  if (!shouldShowOnboarding && !showTour) {
    return null;
  }

  const StepComponent = steps[currentStepIndex].component;
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  return (
    <Modal
      isOpen={true}
      onClose={showTour ? closeTour : () => {}} // Allow closing if in tour mode
      title={
        <div className="flex justify-between items-center w-full">
          <span>{showTour ? 'Quick Tour' : 'Get Started with WorkOrder HQ'}</span>
          {!showTour && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
              Step {currentStepIndex + 1} of {steps.length}
            </span>
          )}
        </div>
      }
      maxWidth="4xl"
      closeOnBackdropClick={showTour}
    >
      <div className="mb-6">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className="bg-accent-color h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <StepComponent onNext={handleNext} onSkip={steps[currentStepIndex].optional ? handleSkip : undefined} />
    </Modal>
  );
};

